// src/pages/Exams/ExamRegistrationDetails.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { supabase } from '../../supabaseClient';

const PARENT_TABLE = 'student_exam_session';
const CHILD_TABLE = 'student_exam_info';

const childRegStatuses = ['Registered', 'Deferred', 'Cancelled'];
const childResStatuses = ['Pending', 'Passed', 'Failed', 'Absent', 'Withheld'];

function pillColor(status) {
  switch (status) {
    case 'Registered':
    case 'registered':
      return 'bg-green-100 text-green-800';
    case 'Deferred':
      return 'bg-yellow-100 text-yellow-800';
    case 'Cancelled':
      return 'bg-red-100 text-red-800';
    case 'Passed':
      return 'bg-emerald-100 text-emerald-800';
    case 'Failed':
      return 'bg-rose-100 text-rose-800';
    case 'Absent':
      return 'bg-gray-200 text-gray-800';
    case 'Withheld':
      return 'bg-purple-100 text-purple-800';
    case 'Pending':
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return d;
  }
}

function ExamRegistrationDetails() {
  const { id } = useParams(); // parent id
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromStudent = searchParams.get('fromStudent'); // student id when navigated from Student Details

  // Detect if user arrived from a /students/:id page but without ?fromStudent param
  const [cameFromStudents, setCameFromStudents] = useState(false);
  useEffect(() => {
    try {
      const ref = document.referrer || '';
      const origin = window.location.origin;
      if (ref.startsWith(origin)) {
        const pathname = new URL(ref).pathname;
        if (/^\/students\/[^/]+/.test(pathname)) setCameFromStudents(true);
      }
    } catch {
      // ignore
    }
  }, []);

  // Back target + label (context-aware)
  const backTarget = fromStudent ? `/students/${fromStudent}?tab=exams` : '/exam-registrations';
  const backLabel = fromStudent ? 'Back to Student Exams' : 'Back to Exam Registrations';

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // Parent & children
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]); // each joined with subject

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setErr('');
    try {
      // Parent
      const { data: p, error: pErr } = await supabase
        .from(PARENT_TABLE)
        .select(
          `
          id, student_id, exam_session_id, registration_status,
          venue, total_fee_paid, payment_date, remarks,
          is_deleted, created_at, updated_at,
          students:students ( id, full_name, email, contact_no ),
          exam_sessions:exam_sessions ( id, name, start_date, end_date )
        `
        )
        .eq('id', id)
        .eq('is_deleted', false)
        .single();
      if (pErr) throw pErr;

      // Children
      const { data: kids, error: cErr } = await supabase
        .from(CHILD_TABLE)
        .select(
          `
          id, session_registration_id, student_id, subject_id, attempt_no,
          registration_status, result_status, notes, is_deleted,
          subjects:subjects ( id, code, name )
        `
        )
        .eq('session_registration_id', id)
        .eq('is_deleted', false)
        .order('subject_id', { ascending: true });
      if (cErr) throw cErr;

      setParent({
        id: p.id,
        student_id: p.student_id,
        student_name: p.students?.full_name || '—',
        student_email: p.students?.email || '—',
        student_contact: p.students?.contact_no || '—',
        exam_session_id: p.exam_session_id,
        exam_session_name: p.exam_sessions?.name || '—',
        exam_session_dates:
          p.exam_sessions?.start_date || p.exam_sessions?.end_date
            ? `${fmtDate(p.exam_sessions?.start_date)} – ${fmtDate(p.exam_sessions?.end_date)}`
            : '—',
        registration_status: p.registration_status,
        venue: p.venue || '—',
        total_fee_paid:
          p.total_fee_paid == null || p.total_fee_paid === ''
            ? '—'
            : `RM${Number(p.total_fee_paid).toLocaleString('en-MY', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
        payment_date: fmtDate(p.payment_date),
        remarks: p.remarks || '—',
        created_at: p.created_at,
        updated_at: p.updated_at,
      });

      // Sort children by numeric part of subject code, then by code, then name
      const sortedChildren = (kids || [])
        .map((r) => ({
          id: r.id,
          subject_id: r.subject_id,
          subject_code: r.subjects?.code || '',
          subject_name: r.subjects?.name || '',
          attempt_no: r.attempt_no || 1,
          registration_status: r.registration_status || 'Registered',
          result_status: r.result_status || 'Pending',
          notes: r.notes || '',
        }))
        .sort((a, b) => {
          const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
          const ac = (a.subject_code || '').trim();
          const bc = (b.subject_code || '').trim();
          const anum = parseInt(ac.replace(/\D+/g, ''), 10);
          const bnum = parseInt(bc.replace(/\D+/g, ''), 10);
          if (!Number.isNaN(anum) && !Number.isNaN(bnum) && anum !== bnum) {
            return anum - bnum; // numeric ASC
          }
          const byCode = collator.compare(ac, bc);
          if (byCode !== 0) return byCode;
          const an = (a.subject_name || '').trim();
          const bn = (b.subject_name || '').trim();
          return collator.compare(an, bn);
        });

      setChildren(sortedChildren);
    } catch (e) {
      console.error(e);
      setErr(e?.message || 'Failed to load registration.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Summary counts (registration + result)
  const summary = useMemo(() => {
    const counts = {
      total: children.length,
      reg: Object.fromEntries(childRegStatuses.map((s) => [s, 0])),
      res: Object.fromEntries(childResStatuses.map((s) => [s, 0])),
    };
    for (const c of children) {
      counts.reg[c.registration_status] = (counts.reg[c.registration_status] || 0) + 1;
      counts.res[c.result_status] = (counts.res[c.result_status] || 0) + 1;
    }
    return counts;
  }, [children]);

  const goBack = () => {
    if (fromStudent) {
      navigate(backTarget);
    } else if (cameFromStudents) {
      navigate(-1);
    } else {
      navigate('/exam-registrations');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Soft-delete this exam registration? This will also hide all its subjects.')) return;
    try {
      // soft-delete parent
      const { error: pErr } = await supabase
        .from(PARENT_TABLE)
        .update({ is_deleted: true })
        .eq('id', id);
      if (pErr) throw pErr;

      // soft-delete children
      const { error: cErr } = await supabase
        .from(CHILD_TABLE)
        .update({ is_deleted: true })
        .eq('session_registration_id', id);
      if (cErr) throw cErr;

      alert('Registration deleted.');
      // Navigate context-aware
      if (fromStudent) {
        navigate(`/students/${fromStudent}?tab=exams`);
      } else if (cameFromStudents) {
        navigate(-1);
      } else {
        navigate('/exam-registrations');
      }
    } catch (e) {
      alert(e?.message || 'Delete failed.');
    }
  };

  if (loading) {
    return <div className="text-center py-10">Loading registration…</div>;
  }

  if (err && !parent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={goBack}>{backLabel}</Button>
          <h1 className="text-xl font-bold text-gray-900">Exam Registration</h1>
        </div>
        <Card>
          <div className="py-10 text-center text-red-600">{err}</div>
        </Card>
      </div>
    );
  }

  if (!parent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={goBack}>{backLabel}</Button>
          <h1 className="text-xl font-bold text-gray-900">Exam Registration</h1>
        </div>
        <Card>
          <div className="py-10 text-center text-gray-600">Registration not found.</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={goBack}>{backLabel}</Button>
          <h1 className="text-xl font-bold text-gray-900">Exam Registration</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/exam-registrations/${parent.id}/edit${fromStudent ? `?fromStudent=${fromStudent}` : ''}`}>
            <Button variant="outline">Edit</Button>
          </Link>
          <Button variant="outline" className="text-red-600 hover:text-red-800" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </div>

      {/* Parent details */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-gray-500">Student</div>
            <div className="font-medium text-gray-900">{parent.student_name}</div>
            <div className="text-xs text-gray-600">{parent.student_email}</div>
            <div className="text-xs text-gray-600">{parent.student_contact}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500">Exam Session</div>
            <div className="font-medium text-gray-900">{parent.exam_session_name}</div>
            <div className="text-xs text-gray-600">{parent.exam_session_dates}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500">Registration Status</div>
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${pillColor(parent.registration_status)}`}>
              {parent.registration_status}
            </span>
          </div>

          <div>
            <div className="text-xs text-gray-500">Total Fee Paid</div>
            <div className="font-medium text-gray-900">{parent.total_fee_paid}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500">Payment Date</div>
            <div className="font-medium text-gray-900">{parent.payment_date}</div>
          </div>

          <div>
            <div className="text-xs text-gray-500">Venue</div>
            <div className="font-medium text-gray-900">{parent.venue}</div>
          </div>

          <div className="md:col-span-2 xl:col-span-3">
            <div className="text-xs text-gray-500">Remarks</div>
            <div className="font-medium text-gray-900">{parent.remarks}</div>
          </div>
        </div>
      </Card>

      {/* Summary */}
      <Card>
        <h2 className="text-lg font-semibold mb-3">Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Registration counts */}
          <div>
            <div className="text-xs text-gray-500 mb-1">Registration</div>
            <div className="flex flex-wrap gap-2">
              {childRegStatuses.map((s) => (
                <span key={s} className={`px-2 py-0.5 text-xs rounded-full ${pillColor(s)}`}>
                  {s}: {summary.reg[s] || 0}
                </span>
              ))}
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800">
                Total: {summary.total}
              </span>
            </div>
          </div>

          {/* Result counts */}
          <div>
            <div className="text-xs text-gray-500 mb-1">Results</div>
            <div className="flex flex-wrap gap-2">
              {childResStatuses.map((s) => (
                <span key={s} className={`px-2 py-0.5 text-xs rounded-full ${pillColor(s)}`}>
                  {s}: {summary.res[s] || 0}
                </span>
              ))}
            </div>
          </div>

          {/* Progress (focus on Passed) */}
          <div>
            <div className="text-xs text-gray-500 mb-1">Progress</div>
            <div className="text-sm text-gray-900">
              Registered: <strong>{summary.reg['Registered']}/{summary.total}</strong>
              <span className="text-gray-500">
                {' '}
                (Deferred:{summary.reg['Deferred'] || 0}, Cancelled:{summary.reg['Cancelled'] || 0})
              </span>
              <br />
              Passed: <strong>{summary.res['Passed'] || 0}/{summary.total}</strong>
              <span className="text-gray-500"> (Pending:{summary.res['Pending'] || 0})</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Subjects table */}
      <Card>
        <h2 className="text-lg font-semibold mb-3">Subjects</h2>
        {children.length === 0 ? (
          <div className="py-6 text-gray-600">No subjects in this registration.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Subject</th>
                  <th className="px-4 py-2 text-left">Attempt #</th>
                  <th className="px-4 py-2 text-left">Reg. Status</th>
                  <th className="px-4 py-2 text-left">Result</th>
                  <th className="px-4 py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {children.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">
                        {r.subject_code ? `${r.subject_code} — ${r.subject_name}` : r.subject_name || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-2">{r.attempt_no}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${pillColor(r.registration_status)}`}>
                        {r.registration_status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${pillColor(r.result_status)}`}>
                        {r.result_status}
                      </span>
                    </td>
                    <td className="px-4 py-2">{r.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default ExamRegistrationDetails;
